import { useAutoScroll } from "@/hooks/useAutoScroll";
import React, { useEffect, useRef, useState } from "react";

/**
 * FilmWall — GirlyGrove Films Vol. 01
 * ------------------------------------
 * A portrait (9:16) auto-scrolling film slider.
 *  • Each film shows in its native vertical aspect — no cropping.
 *  • Continuous slow left drift (marquee) with hover to pause.
 *  • Mobile: ~1.6 films visible at once, swipeable + auto-drifts.
 *  • Desktop: 3-4 films visible, auto-drifts smoothly.
 *  • Non-clickable — pure ambient cinema.
 */

const FILMS = [
  {
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_grove-design/artifacts/pl7oghex_kling_20260626_VIDEO_KLING_AI_M_4192_0.mp4",
    chapter: "I",
    title: "The Muse",
    subtitle: "Golden hour, quiet armour",
  },
  {
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_grove-design/artifacts/oto2qnaf_kling_20260626_VIDEO_Lake_Como__4226_0.mp4",
    chapter: "II",
    title: "Lake Como",
    subtitle: "Where the light writes love notes",
  },
  {
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_grove-design/artifacts/9qjm3m7m_kling_20260626_VIDEO_Flower_mar_4397_0.mp4",
    chapter: "III",
    title: "Flower Market",
    subtitle: "Petals & polished gold",
  },
  {
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_grove-design/artifacts/xgoh60hh_kling_20260626_VIDEO_Lake_Como__4295_0.mp4",
    chapter: "IV",
    title: "On the Terrace",
    subtitle: "A summer that never ends",
  },
  {
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_grove-design/artifacts/5y04qywn_kling_20260626_VIDEO_Theek_hai__4410_0.mp4",
    chapter: "V",
    title: "Her Moment",
    subtitle: "The girl who decided",
  },
];

const FilmCard = ({ film }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "150% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const v = videoRef.current;
    if (!v) return;
    try { v.load(); } catch (e) { /* ignore */ }
    const t = setTimeout(() => v.play?.().catch(() => {}), 120);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <div
      ref={containerRef}
      data-testid={`filmwall-card-${film.chapter}`}
      className="film-card flex-shrink-0 w-[68vw] sm:w-[300px] lg:w-[340px]"
    >
      {/* Portrait video frame (9:16) */}
      <div
        className="relative overflow-hidden bg-plum group"
        style={{ aspectRatio: "9 / 16", borderRadius: "10px" }}
      >
        {active ? (
          <video
            ref={videoRef}
            src={film.src}
            className="w-full h-full object-cover fw-kenburns"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg,#3a1e28 0%,#5a2f3f 55%,#8b4a5c 100%)",
            }}
          />
        )}
        {/* Gold hairline frame */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ boxShadow: "inset 0 0 0 1px rgba(232,200,138,0.35)", borderRadius: "10px" }}
        />
      </div>

      {/* Editorial caption BELOW the video (no crop, no overlay) */}
      <div className="mt-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="h-px w-5 bg-[#E8C88A]/70" />
          <span className="text-[9px] tracking-[0.42em] uppercase text-[#E8C88A]/85 font-medium">
            Ch. {film.chapter}
          </span>
          <span className="h-px w-5 bg-[#E8C88A]/70" />
        </div>
        <p className="font-serif italic leading-none text-cream text-xl">
          {film.title}
        </p>
        <p className="text-[10px] tracking-[0.24em] uppercase text-cream/60 mt-2">
          {film.subtitle}
        </p>
      </div>
    </div>
  );
};

const FilmWall = () => {
  // Duplicate the list so the marquee loops seamlessly
  const track = [...FILMS, ...FILMS];
  const railRef = useAutoScroll(0.45);

  return (
    <section
      className="relative w-full py-16 lg:py-24 overflow-hidden"
      style={{ background: "#0B070A" }}
      data-testid="film-wall"
    >
      <style>{`
        @keyframes fwKenBurns {
          0%   { transform: scale(1.05) translate(0, 0); }
          50%  { transform: scale(1.13) translate(-1%, -1%); }
          100% { transform: scale(1.05) translate(0, 0); }
        }
        .fw-kenburns { animation: fwKenBurns 22s ease-in-out infinite; will-change: transform; }

      `}</style>

      {/* Section header */}
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 mb-10 lg:mb-14">
        <div className="text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="h-px w-10 bg-[#C5985A]/60" />
            <span className="text-[10px] sm:text-[11px] tracking-[0.55em] uppercase text-[#C5985A]/85 font-medium">
              GirlyGrove Films · Volume 01
            </span>
            <span className="h-px w-10 bg-[#C5985A]/60" />
          </div>
          <h2
            className="font-serif leading-[1.02] tracking-tight text-cream"
            style={{ fontSize: "clamp(34px, 4.6vw, 60px)" }}
          >
            Five little{" "}
            <span className="italic text-[#E8A0B4]">love letters</span>
          </h2>
          <p className="mt-4 text-cream/60 max-w-[520px] mx-auto text-[14px] tracking-wide">
            Slow cinema for the jewelry drawer — press pause on the day and watch light
            find gold, again and again.
          </p>
        </div>
      </div>

      {/* Portrait film marquee */}
      <div className="relative" data-testid="film-marquee">
        <div ref={railRef} className="overflow-x-auto no-scrollbar select-none cursor-grab active:cursor-grabbing" data-testid="film-rail">
          <div className="flex gap-5 lg:gap-6 w-max pl-5 lg:pl-10">
            {track.map((f, idx) => (
              <FilmCard key={`${f.chapter}-${idx}`} film={f} />
            ))}
          </div>
        </div>

        {/* Edge fades */}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 bottom-0 left-0 w-16 lg:w-24"
          style={{
            background:
              "linear-gradient(to right, #0B070A 0%, rgba(11,7,10,0) 100%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 bottom-0 right-0 w-16 lg:w-24"
          style={{
            background:
              "linear-gradient(to left, #0B070A 0%, rgba(11,7,10,0) 100%)",
          }}
        />
      </div>

      {/* Bottom marker */}
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 mt-10 text-center">
        <span className="text-[10px] tracking-[0.4em] uppercase text-[#C5985A]/70">
          Swipe or drag to browse · Hover to pause
        </span>
      </div>
    </section>
  );
};

export default FilmWall;
